import React, { FC, useState, useEffect } from 'react';
import FormGroup from 'components/form/FormGroup';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import styled from 'styled-components';
import { FaEdit, FaRegSave, FaFolderOpen } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { IDoc } from '../types';

interface IProps {
  firstDoc: IDoc;
  isMultilingual: boolean;
  methods: any;
  isEditable?: boolean;
  handleEditModeChange?: () => void;
}

type StyleProps = {
  isEditable?: boolean;
};

const HolderWrapperComponent: FC<IProps> = ({
  firstDoc,
  methods,
  isMultilingual,
  isEditable,
  handleEditModeChange,
}: IProps) => {
  const { t } = useTranslation();
  const { document_id: docId } = firstDoc;
  const [wasManuallyEdited, setWasManuallyEdited] = useState(false);
  const renderDocIcon = () => {
    return (
      <TooltipWrapper
        id="tooltip-internalDoc"
        overlay={t('documents.internalDoc')}
      >
        <FaFolderOpen
          size={18}
          color="#32bfa3"
        />
      </TooltipWrapper>
    );
  };

  const docName = methods.watch(`doc-${docId}-name`);
  const docNameEn = methods.watch(`doc-${docId}-name-en`);
  let docHolderName = docName;
  let docHolderNameEn = docNameEn;

  useEffect(() => {
    if (!wasManuallyEdited) {
      methods.setValue('doc-holder-name', docName);
      if (isMultilingual) {
        methods.setValue('doc-holder-name-en', docNameEn);
      }
    }
  }, [docName, docNameEn, wasManuallyEdited]);

  docHolderName = methods.watch('doc-holder-name');
  docHolderNameEn = methods.watch('doc-holder-name-en');

  return (
    <FileWrapper>
      <FileRow>
        <FileDetails>
          <FolderTitle>{t('documents.packageName')}:</FolderTitle>
          {isEditable ? (
            <>
              <Forty isEditable={isEditable}>
                {renderDocIcon()}
                <FormGroup
                  controlId="formGroupName"
                  label={t('documents.packageName')}
                  type="text"
                  name="doc-holder-name"
                  isRequired
                  formGroupClassName="d-inline-block ml-1 full-wrapper"
                />
                {isMultilingual && (
                  <FormGroup
                    controlId="formGroupName"
                    label={t('documents.packageName2')}
                    type="text"
                    name="doc-holder-name-en"
                    isRequired
                    formGroupClassName="d-inline-block ml-1 full-wrapper"
                  />
                )}
              </Forty>
            </>
          ) : (
            <>
              <Forty isEditable={isEditable}>
                {renderDocIcon()}
                <DocTitle className="mb-0 ml-2 d-inline-block">
                  {docHolderName}
                  {isMultilingual ? ` / ${docHolderNameEn}` : null}
                </DocTitle>
              </Forty>
            </>
          )}
        </FileDetails>
        <EditButton isEditable={isEditable}>
          <span className="ml-2 ml-2-sm-none">
            {isEditable && (
              <TooltipWrapper
                id="tooltip-save"
                overlay={t('common.save')}
              >
                <Button
                  btnType="info"
                  size="sm"
                  className="save-btn"
                  outlined
                  onClick={() => {
                    if (handleEditModeChange) handleEditModeChange();
                    setWasManuallyEdited(true);
                  }}
                  style={{ marginBottom: '-30px' }}
                >
                  <FaRegSave size={14} />
                </Button>
              </TooltipWrapper>
            )}
          </span>
          {!isEditable && (
            <TooltipWrapper
              id="tooltip-edit"
              overlay={t('common.edit')}
            >
              <Button
                btnType="primary"
                size="sm"
                outlined
                onClick={handleEditModeChange}
              >
                {t('docWizard.editBtn')}
              </Button>
            </TooltipWrapper>
          )}
        </EditButton>
      </FileRow>
    </FileWrapper>
  );
};

const FileWrapper = styled.div`
  padding: 2rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const FileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${props => props.theme.breakpoints.sm} {
    flex-direction: column-reverse;
  }
`;

const FileDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  width: 100%;

  @media ${props => props.theme.breakpoints.sm} {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const Forty = styled.div<StyleProps>`
  width: ${props => (props.isEditable ? '76%' : '70%')};

  display: flex;
  align-items: center;

  .full-wrapper {
    width: 100%;
  }
`;

const DocTitle = styled.h5`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FolderTitle = styled.h5`
  margin-bottom: 0;
`;

const EditButton = styled.div<StyleProps>`
  width: ${props => (props.isEditable ? '5%' : '35%')};
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media ${props => props.theme.breakpoints.sm} {
    width: 100%;
    justify-content: flex-start;

    .ml-2-sm-none {
      margin-left: 0 !important;
    }

    .save-btn {
      margin-top: -30px;
    }
  }
`;

export default HolderWrapperComponent;
